/**
 * Part payments on loan installments. The server is the source of truth (it re-checks every rule);
 * this mirrors it so the forms can explain a problem before anything is sent.
 *  - the smallest payment is MIN_PARTIAL_AMOUNT, unless what is left on the installment is smaller
 *  - a payment can be larger than the installment: the extra goes to the next installments
 *  - nothing above what the whole loan still owes is accepted
 */
import { formatNaira } from "../../utils/dashboardFormat";

export const MIN_PARTIAL_AMOUNT = 100;

/** Loan type choice: follow the cooperative setting, or force it on or off for this type. */
export const PARTIAL_OVERRIDE_OPTIONS = [
  { value: "default", label: "Use the cooperative setting" },
  { value: "allow", label: "Allow part payments" },
  { value: "block", label: "Require the full installment" },
];

export function overrideFromLoanType(value) {
  if (value === true || value === 1 || value === "1") return "allow";
  if (value === false || value === 0 || value === "0") return "block";
  return "default";
}

export function overrideToPayload(choice) {
  if (choice === "allow") return true;
  if (choice === "block") return false;
  return null;
}

const toMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

/** What is left on one installment row from the API (balance when the server sent it). */
export function installmentBalance(row) {
  if (!row) return 0;
  if (row.balance !== undefined && row.balance !== null) return toMoney(row.balance);
  return Math.max(0, toMoney(toMoney(row.amount) - toMoney(row.amount_paid)));
}

/**
 * @returns {string} an error message, or "" when the amount can be submitted
 * @param amount what the person typed (number or string)
 * @param balance what is left on the chosen installment
 * @param outstanding what is left on the whole loan (only used when part payments are on)
 * @param enabled whether part payments are on for this loan
 */
export function validatePaymentAmount({ amount, balance, outstanding, enabled }) {
  const value = toMoney(amount);
  const left = toMoney(balance);

  if (!(value > 0)) return "Enter an amount greater than zero";

  if (!enabled) {
    return Math.abs(value - left) > 0.005
      ? `Part payments are not enabled for this loan. Pay the full balance of ${formatNaira(left)}`
      : "";
  }

  const owed = toMoney(outstanding);
  if (owed > 0 && value - owed > 0.005) {
    return `That is more than you owe on this loan (${formatNaira(owed)})`;
  }

  const minimum = Math.min(MIN_PARTIAL_AMOUNT, left);
  if (value + 0.005 < minimum) return `The smallest payment is ${formatNaira(minimum)}`;

  return "";
}

/** Short line explaining where a payment goes, shown under the amount box. */
export function paymentEffect({ amount, balance, enabled }) {
  const value = toMoney(amount);
  const left = toMoney(balance);
  if (!enabled || !(value > 0)) return "";
  if (value + 0.005 < left) return `${formatNaira(toMoney(left - value))} will remain on this installment`;
  if (Math.abs(value - left) <= 0.005) return "This clears the installment";
  return `${formatNaira(toMoney(value - left))} of this goes to the next installment`;
}
